import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { fetchBySlug, fetchCollection, optimizeImage } from '../lib/cms';
import VideoFacade from '../components/VideoFacade';
import CategoryBadge from '../components/CategoryBadge';
import HelpNotice from '../components/HelpNotice';

export default function PresseFilmDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function loadItem() {
      setLoading(true);
      try {
        let entry = await fetchBySlug(slug);
        if (!entry || entry.collectionName !== 'presse_film') {
          const list = await fetchCollection('presse_film', { limit: 50 });
          entry = (list.items || []).find((m) => (m.slug?._ || m.slug) === slug);
        }
        if (isMounted && entry) {
          setItem(entry);
        }
      } catch (e) {
        console.warn('Error loading media detail', e);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadItem();
    return () => { isMounted = false; };
  }, [slug]);

  const seoTitle = item?.seo_title || item?.title || 'Presse & Film';
  const seoDescription = item?.seo_description || item?.description || '';

  useSeo({
    title: seoTitle,
    description: seoDescription,
  });

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="font-serif text-slate-500 flex items-center gap-2">
          <i className="fa-solid fa-spinner fa-spin text-[#fd7302]" aria-hidden="true" />
          <span>Henter medieindhold...</span>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white px-4 text-center">
        <h1 className="font-serif text-3xl font-bold text-[#010b27]">
          Indholdet blev ikke fundet
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Det efterspurgte presse- eller filmindslag findes ikke.
        </p>
        <Link
          to="/presse-og-film"
          className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[#010b27] px-5 py-2.5 text-xs font-semibold text-white hover:bg-[#fd7302]"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          <span>Tilbage til Presse & Film</span>
        </Link>
      </div>
    );
  }

  const isFilm = item.media_type === 'film';

  return (
    <div className="min-h-screen bg-white pb-16">
      {/* Breadcrumbs */}
      <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-1.5 sm:py-2.5 text-[11px] sm:text-xs text-slate-500">
        <div className="mx-auto max-w-7xl flex items-center gap-1.5 sm:gap-2 overflow-hidden">
          <Link to="/" className="hover:text-[#010b27] shrink-0">Forside</Link>
          <span className="text-slate-400 shrink-0">/</span>
          <Link to="/presse-og-film" className="hover:text-[#010b27] shrink-0">Presse & Film</Link>
          <span className="text-slate-400 shrink-0">/</span>
          <span className="truncate text-[#010b27] font-medium">{item.title}</span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pt-8 sm:pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-12 items-start">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="rounded-xs bg-[#fd7302] px-2.5 py-0.5 text-xs font-bold text-white uppercase tracking-wider">
                {isFilm ? 'Videoanalyse' : 'Pressemeddelelse'}
              </span>
              <CategoryBadge audience={item.audience} size="sm" />
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-black text-[#010b27] leading-tight">
              {item.title}
            </h1>

            <div className="mt-4 pb-4 border-b border-slate-200 flex items-center justify-between text-xs text-slate-500">
              <time dateTime={item.publish_date}>
                Publiceret: {item.publish_date}
              </time>
              {item.duration && (
                <span className="font-mono">
                  <i className="fa-solid fa-clock mr-1 text-[#fd7302]" aria-hidden="true" />
                  {item.duration}
                </span>
              )}
            </div>

            {/* Media Presentation */}
            <div className="my-8">
              {isFilm && item.video_url ? (
                <VideoFacade
                  videoUrl={item.video_url}
                  coverImage={item.cover_image}
                  title={item.title}
                  duration={item.duration}
                />
              ) : item.cover_image?.[0] ? (
                <div className="overflow-hidden rounded-md border border-slate-200">
                  <img
                    src={optimizeImage(item.cover_image[0], { width: 1200 })}
                    alt={item.title}
                    className="w-full aspect-16/9 object-cover"
                  />
                </div>
              ) : null}
            </div>

            {/* Lead description */}
            {item.description && (
              <div className="my-6 border-l-4 border-[#fd7302] pl-4 sm:pl-6 py-1 bg-slate-50/60 rounded-r-sm">
                <p className="font-serif text-lg sm:text-xl text-[#010b27] italic leading-relaxed">
                  {item.description}
                </p>
              </div>
            )}

            {/* Rich text body content with clear typography */}
            {item.content && (
              <div
                className="my-8 prose article-content max-w-none text-[#1e293b]"
                dangerouslySetInnerHTML={{ __html: item.content }}
              />
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            <div className="rounded-md border border-slate-200 bg-white p-5 shadow-xs">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4">
                <i className="fa-solid fa-film text-[#fd7302]" aria-hidden="true" />
                <h3 className="font-serif text-base font-bold text-[#010b27]">
                  Om mediet
                </h3>
              </div>
              <dl className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Format</dt>
                  <dd className="font-semibold text-[#010b27] capitalize">{item.media_type || 'Film'}</dd>
                </div>
                {item.duration && (
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-500">Længde</dt>
                    <dd className="font-semibold text-[#010b27]">{item.duration}</dd>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Publiceret</dt>
                  <dd className="font-semibold text-[#010b27]">{item.publish_date}</dd>
                </div>
              </dl>
              <div className="mt-4 pt-3 border-t border-slate-100">
                <Link
                  to="/presse-og-film"
                  className="text-xs font-bold text-[#fd7302] hover:underline inline-flex items-center gap-1.5"
                >
                  <span>Se alle produktioner</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/70 p-5 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#fd7302]">
                Pressekontakt
              </span>
              <h4 className="mt-1 font-serif text-base font-bold text-[#010b27]">
                Skatunderlup Presse
              </h4>
              <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                For redaktionelle henvendelser, pressebilleder og interviewaftaler bedes du kontakte redaktionen.
              </p>
              <Link
                to="/kontakt"
                className="mt-4 inline-flex items-center justify-center w-full gap-2 rounded-xs bg-[#010b27] hover:bg-[#021447] px-3.5 py-2 text-xs font-bold text-white transition-colors"
              >
                <span>Kontakt redaktionen</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </div>

        {/* Press / Editorial Contact Box */}
        <div className="mt-12 rounded-md border border-slate-200 bg-slate-50/70 p-6 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h4 className="font-serif text-lg font-bold text-[#010b27]">
                Pressehenvendelser vedrørende dette emne
              </h4>
              <p className="mt-1 text-xs text-slate-600">
                Journalister er velkomne til at kontakte redaktionen for uddybende kommentarer.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="mailto:mmkh@lexnovo.dk"
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#010b27] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#fd7302] transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope" aria-hidden="true" />
                <span>mmkh@lexnovo.dk</span>
              </a>
              <a
                href="tel:61444604"
                className="inline-flex items-center gap-1.5 rounded-sm border border-[#010b27] px-3.5 py-2 text-xs font-semibold text-[#010b27] hover:bg-[#010b27] hover:text-white transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>61 44 46 04</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <HelpNotice variant="banner" />
        </div>
      </div>
    </div>
  );
}
